import { Sort_Enum } from '@weave/schema-gen-ts/dist/schemas/sms/draft/v1/draft_service.pb';
import { InfiniteData, UseMutationOptions, useQueryClient } from 'react-query';
import { useMutation } from '@frontend/react-query-helpers';
import { queryKeys } from './queries';
import { SchemaSMSDraftService } from './service';
import { DeleteIO, ListDraftsIO, UpsertIO } from './types';

export const mutationKeys = {
  baseKey: ['sms-draft'],
  upsertDraft: () => [...mutationKeys.baseKey, 'upsert'],
  deleteDraft: () => [...mutationKeys.baseKey, 'delete'],
};

const isInfiniteData = <T>(data: unknown): data is InfiniteData<T> => {
  return typeof data === 'object' && data !== null && 'pages' in data && Array.isArray((data as InfiniteData<T>).pages);
};

type MutationContext<C = unknown> = {
  myContext?: string;
  optionContext?: C;
};

// The BE is no longer wanting to hydrate the person, on the `UpsertDraftResponse`
type UpsertDraftRequestOverride = UpsertIO['input'] & Pick<UpsertIO['output'], 'person'>;

export type UseUpsertDraftMutationOptions<E = unknown, C = unknown> = UseMutationOptions<
  UpsertIO['output'],
  E,
  UpsertDraftRequestOverride,
  C | undefined
>;
export const useUpsertDraftMutation = <E = unknown, C = unknown>({
  onMutate,
  onSuccess,
  onError,
  onSettled,
  ...options
}: UseUpsertDraftMutationOptions<E, C> = {}) => {
  const queryClient = useQueryClient();

  return useMutation<UpsertIO['output'], E, UpsertDraftRequestOverride, MutationContext<C>>({
    mutationKey: mutationKeys.upsertDraft(),
    mutationFn: ({ person: _, ...req }) => SchemaSMSDraftService.Upsert(req),
    ...options,
    onMutate: async (req) => {
      return {
        myContext: '',
        optionContext: await onMutate?.(req),
      };
    },
    onSuccess: (res, req, context) => {
      const targetThreadId = res.threadId || req.threadId;

      // Update GetDraft cache
      const getDraftKey = queryKeys.getDraft({
        orgId: req.orgId,
        threadId: targetThreadId,
        userId: req.userId,
      });
      queryClient.setQueriesData(
        {
          queryKey: getDraftKey,
          exact: false,
          predicate: ({ queryKey }) => {
            if (Array.isArray(queryKey)) {
              return queryKey.some(
                (item) => typeof item === 'object' && item !== null && item['threadId'] === targetThreadId
              );
            }
            return false;
          },
        },
        { draftId: res.draftId, draft: res.draft }
      );

      queryClient.invalidateQueries({
        queryKey: queryKeys.listDraftsCount({
          orgId: req.orgId,
          userId: req.userId,
        }),
        exact: false,
      });

      const newDraft: ListDraftsIO['output']['drafts'][number] = {
        ...res,
        threadId: targetThreadId,
        draft: res.draft ?? req.draft,
        updatedAt: res.updatedAt || new Date().toISOString(),
        locationId: res.locationId || req.locationId || '',
        locationPhone: res.locationPhone || req.locationPhone || '',
        personPhone: res.personPhone || req.personPhone || '',
        departmentId: res.departmentId || req.departmentId || '',
        threadType: res.threadType ?? req.threadType,
        groupName: res.groupName ?? req.groupName,
        person: req.person, // The BE is no longer wanting to hydrate the person, on the `UpsertDraftResponse`
      };

      const listDraftBaseKey = queryKeys.listDrafts({
        orgId: req.orgId,
        userId: req.userId,
      });

      const matchingQueries = queryClient.getQueryCache().findAll({
        queryKey: listDraftBaseKey,
        exact: false,
        predicate: (query) => query.state.data !== undefined,
      });

      matchingQueries.forEach((query) => {
        const queryKeyParams = Array.isArray(query.queryKey)
          ? (query.queryKey[2] as ListDraftsIO['input'] | undefined)
          : undefined;
        const sortOrder = queryKeyParams?.sort;

        // Verify if newDraft matches the query's filters
        const matchesFilters = (() => {
          if (!queryKeyParams) return true;

          if (
            queryKeyParams.threadType !== undefined &&
            newDraft.threadType !== undefined &&
            queryKeyParams.threadType !== newDraft.threadType
          ) {
            return false;
          }

          if (
            queryKeyParams.locationPhones?.length &&
            newDraft.locationPhone &&
            !queryKeyParams.locationPhones.includes(newDraft.locationPhone)
          ) {
            return false;
          }

          if (queryKeyParams.locationId && newDraft.locationId && queryKeyParams.locationId !== newDraft.locationId) {
            if (queryKeyParams.groupIds?.length && !queryKeyParams.groupIds.includes(newDraft.locationId)) {
              return false;
            }
            if (!queryKeyParams.groupIds?.length) {
              return false;
            }
          }

          return true;
        })();

        queryClient.setQueryData<InfiniteData<ListDraftsIO['output']> | ListDraftsIO['output'] | undefined>(
          query.queryKey,
          (oldData) => {
            if (!oldData) return undefined;

            // Handle InfiniteData structure
            if (isInfiniteData<ListDraftsIO['output']>(oldData)) {
              const pagesWithoutDraft: ListDraftsIO['output'][] = oldData.pages.map((page) => ({
                ...page,
                drafts: (page.drafts ?? []).filter((draft) => draft.threadId !== targetThreadId),
              }));

              if (!matchesFilters) {
                return {
                  ...oldData,
                  pages: pagesWithoutDraft,
                };
              }

              if (oldData.pages.length === 0) {
                return {
                  ...oldData,
                  pages: [{ drafts: [newDraft], nextPageToken: '' }],
                  pageParams: oldData.pageParams.length ? oldData.pageParams : [undefined],
                };
              }

              if (sortOrder === Sort_Enum.ASC) {
                const lastIdx = pagesWithoutDraft.length - 1;
                const targetPage = pagesWithoutDraft[lastIdx];
                // Only append to the last page if all pages are fetched (no nextPageToken remaining on server).
                if (targetPage && !targetPage.nextPageToken) {
                  pagesWithoutDraft[lastIdx] = {
                    ...targetPage,
                    drafts: [...(targetPage.drafts ?? []), newDraft],
                  };
                }
              } else {
                // DESC or default: Prepend to first page
                const firstPage = pagesWithoutDraft[0];
                if (firstPage) {
                  pagesWithoutDraft[0] = {
                    ...firstPage,
                    drafts: [newDraft, ...(firstPage.drafts ?? [])],
                  };
                }
              }

              return {
                ...oldData,
                pages: pagesWithoutDraft,
              };
            }

            // Handle standard ListDraftsIO['output'] structure
            const oldList = oldData.drafts?.filter((draft) => draft.threadId !== targetThreadId) ?? [];

            if (!matchesFilters) {
              return {
                ...oldData,
                drafts: oldList,
              };
            }

            return {
              ...oldData,
              drafts: sortOrder === Sort_Enum.ASC ? [...oldList, newDraft] : [newDraft, ...oldList],
            };
          }
        );
      });

      // Honor provided onSuccess
      onSuccess?.(res, req, context?.optionContext);
    },
    onError: (error, req, context) => onError?.(error, req, context?.optionContext),
    onSettled: (res, error, req, context) => onSettled?.(res, error, req, context?.optionContext),
  });
};

export type UseDeleteDraftMutationOptions<E = unknown, C = unknown> = UseMutationOptions<
  DeleteIO['output'],
  E,
  DeleteIO['input'],
  C
>;
export const useDeleteDraftMutation = <E = unknown, C = unknown>(options?: UseDeleteDraftMutationOptions<E, C>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: mutationKeys.deleteDraft(),
    mutationFn: (req) => SchemaSMSDraftService.Delete(req),
    onMutate: async (req) => {
      return options?.onMutate?.(req);
    },
    onSuccess: (res, req, ...rest) => {
      // Update GetDraft cache
      const getDraftKey = queryKeys.getDraft({
        orgId: req.orgId,
        threadId: req.threadId,
        userId: req.userId,
      });
      queryClient.invalidateQueries({ queryKey: getDraftKey, exact: false });

      // Update ListDrafts cache (only for queries that already have cached data)
      const listDraftsKey = queryKeys.listDrafts({
        orgId: req.orgId,
        userId: req.userId,
      });
      queryClient.setQueriesData<InfiniteData<ListDraftsIO['output']> | ListDraftsIO['output'] | undefined>(
        {
          exact: false,
          queryKey: listDraftsKey,
          predicate: (query) => query.state.data !== undefined,
        },
        (oldData) => {
          if (!oldData) return undefined;
          if (isInfiniteData<ListDraftsIO['output']>(oldData)) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                drafts: (page.drafts ?? []).filter(({ threadId }) => threadId !== req.threadId),
              })),
            };
          }
          return {
            ...oldData,
            drafts: oldData.drafts?.filter(({ threadId }) => threadId !== req.threadId) ?? [],
          };
        }
      );

      queryClient.invalidateQueries({
        queryKey: queryKeys.listDraftsCount({
          orgId: req.orgId,
          userId: req.userId,
        }),
        exact: false,
      });

      // Honor provided onSuccess
      options?.onSuccess?.(res, req, ...rest);
    },
    ...options,
  });
};
